export type IncomeType =
  | 'salary'
  | 'bonus'
  | 'freelance'
  | 'investment'
  | 'other';

export type ExpenseType = 'one_time' | 'recurring' | 'installment';

export interface User {
  id: number;
  name: string;
  email: string;
}

export interface Category {
  id: number;
  name: string;
  slug: string | null;
  color: string;
  icon: string | null;
  isDefault: boolean;
}

export interface Owner {
  id: number;
  name: string;
  phone: string | null;
  email: string | null;
  notes: string | null;
}

export interface OwnerWithDebt extends Owner {
  projectCount: number;
  activeProjectCount: number;
  totalCommitted: number;
  totalPaid: number;
  totalRemaining: number;
}

export interface Income {
  id: number;
  userId: number;
  year: number;
  month: number;
  type: IncomeType;
  amount: string | number;
  description: string | null;
}

export interface Expense {
  id: number;
  userId: number;
  name: string;
  categoryId: number;
  category: Category;
  amount: string | number;
  year: number;
  month: number;
  expenseType: ExpenseType;
  recurringGroupId: number | null;
  installmentGroupId: number | null;
  installmentNumber: number | null;
  totalInstallments: number | null;
  suggestionKey: string | null;
  notes: string | null;
  isPaid: boolean;
}

export interface ExpenseSuggestion {
  key: string;
  name: string;
  icon: string;
  categoryId: number | null;
  category: Category | null;
}

export interface ProjectedExpense {
  id: number | null;
  name: string;
  categoryId: number;
  category: Category;
  amount: number;
  year: number;
  month: number;
  expenseType: ExpenseType;
  status: 'actual' | 'projected';
  installmentNumber: number | null;
  totalInstallments: number | null;
  suggestionKey: string | null;
  isPaid: boolean;
}

export interface CategoryBreakdown {
  categoryId: number;
  category: Category;
  total: number;
  actual: number;
  projected: number;
}

export interface MonthlyReport {
  year: number;
  month: number;
  totalIncome: number;
  totalExpenses: number;
  totalActual: number;
  totalProjected: number;
  balance: number;
  categoryBreakdown: CategoryBreakdown[];
  expenses: ProjectedExpense[];
}

export interface MonthComparison {
  year: number;
  month: number;
  totalIncome: number;
  totalExpenses: number;
  balance: number;
}

export type ProjectStatus = 'active' | 'completed' | 'cancelled';

export interface ProjectSummary {
  id: number;
  name: string;
  description: string | null;
  categoryId: number;
  category: Category;
  ownerId: number | null;
  owner: Owner | null;
  totalAmount: number;
  paidAmount: number;
  remainingAmount: number;
  progressPercent: number;
  status: ProjectStatus;
  paymentsCount: number;
  createdAt: string;
}

export interface ProjectPayment {
  id: number;
  amount: number;
  paidOn: string;
  notes: string | null;
  expenseId: number | null;
}

export interface ProjectDetail extends ProjectSummary {
  payments: ProjectPayment[];
}

export interface ProjectsOverview {
  activeCount: number;
  totalOutstanding: number;
  totalCommitted: number;
  totalPaid: number;
  projects: ProjectSummary[];
}

export interface DashboardSummary {
  current: MonthlyReport;
  previous: MonthlyReport;
  incomeChange: number;
  expenseChange: number;
  last6Months: MonthComparison[];
  projects: ProjectsOverview;
}

export const INCOME_TYPE_LABELS: Record<IncomeType, string> = {
  salary: 'Salário',
  bonus: 'Bônus',
  freelance: 'Freelance',
  investment: 'Investimentos',
  other: 'Outros',
};

export const EXPENSE_TYPE_LABELS: Record<ExpenseType, string> = {
  one_time: 'Única',
  recurring: 'Recorrente',
  installment: 'Parcelada',
};
